/**
 * Supplier Import API — RTK Query slice for extract + import.
 * Uses same /api/v1/imports/parties/insert endpoint as customers, with partyType = 'SUPPLIER'.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type { components, operations, paths } from '../../../api/types/api';

// -----------------------------------------------------------------------------
// Types (from OpenAPI — api.ts)
// -----------------------------------------------------------------------------

export type SupplierExtractRequestDto =
  paths['/api/v1/imports/excel/extract/supplier']['post']['requestBody']['content']['application/json'];
export type SupplierExtractResponseDto =
  paths['/api/v1/imports/excel/extract/supplier']['post']['responses']['200']['content']['application/json'];

export type SupplierImportRequestDto =
  paths['/api/v1/imports/parties/insert']['post']['requestBody']['content']['application/json'];
export type SupplierImportResponseDto =
  paths['/api/v1/imports/parties/insert']['post']['responses']['200']['content']['application/json'];
export type SupplierImportRowErrorDto = components['schemas']['PartyImportRowErrorDto'];

export type SaveSupplierDraftRequestDto = components['schemas']['SaveImportDraftRequestDto'];
export type SaveSupplierDraftResponseDto =
  operations['ImportsController_saveDraft']['responses'][200]['content']['application/json'];
export type GetSupplierDraftResponseDto =
  operations['ImportsController_getSupplierDraft']['responses'][200]['content']['application/json'];
export type DeleteSupplierDraftResponseDto =
  operations['ImportsController_deleteDraft']['responses'][200]['content']['application/json'];

// -----------------------------------------------------------------------------
// API definition & export
// -----------------------------------------------------------------------------

export const supplierImportApi = createApi({
  reducerPath: 'supplierImportApi',
  baseQuery: nestBaseQuery,
  endpoints: (builder) => ({
    extractSupplierExcel: builder.mutation<SupplierExtractResponseDto, SupplierExtractRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/excel/extract/supplier', { body: arg }),
    }),
    /** Bulk insert parties (suppliers) from extract payload. */
    importSuppliers: builder.mutation<SupplierImportResponseDto, SupplierImportRequestDto>({
      query: (body) =>
        client.POST('/api/v1/imports/parties/insert', {
          body,
        }),
    }),
    saveSupplierDraft: builder.mutation<SaveSupplierDraftResponseDto, SaveSupplierDraftRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/drafts/save', {
          body: arg,
        }),
    }),
    getSupplierDraft: builder.query<GetSupplierDraftResponseDto, void>({
      query: () => client.GET('/api/v1/imports/drafts/suppliers'),
    }),
    deleteSupplierDraft: builder.mutation<DeleteSupplierDraftResponseDto, void>({
      query: () =>
        client.DELETE('/api/v1/imports/drafts/{importType}', {
          params: { path: { importType: 'SUPPLIER' } },
        }),
    }),
  }),
});

export const {
  useExtractSupplierExcelMutation,
  useImportSuppliersMutation,
  useSaveSupplierDraftMutation,
  useLazyGetSupplierDraftQuery,
  useDeleteSupplierDraftMutation,
} = supplierImportApi;
